import { inject, Injectable } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import {
  ConfirmationService,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { map, Observable } from 'rxjs';
import {
  BoxQuestionnaireQuestion,
  BoxQuestionnaireQuestionOption,
  BoxQuestionnaireQuestionOptionAttribute,
} from '@web/shared/module/shop';
import { Guid, valueOrDefault } from '@web/utils';
import { AdminBoxQuestionnaireApiService } from '../api/services/admin-box-questionnaire-api.service';
import { AdminBoxQuestionnaireCollectionFilter } from '../models/admin-box-questionnaire-collection-filter';
import { AdminBoxQuestionnaireCollectionItem } from '../models/admin-box-questionnaire-collection-item';
import { AdminBoxQuestionnaireContextOperations } from '../models/admin-box-questionnaire-context-operations';
import { AdminBoxQuestionnaireDetails } from '../models/admin-box-questionnaire-details';
import { AdminBoxQuestionnaireChangeSelectionRequest } from '../api/requests/admin-box-questionnaire-change-selection.request';

@Injectable({
  providedIn: 'root',
})
export class AdminBoxQuestionnaireDataSourceService {
  /* prettier-ignore */
  private readonly api = inject(AdminBoxQuestionnaireApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly confirmation = inject(ConfirmationService);
  private readonly toast = inject(ToastService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminBoxQuestionnaireCollectionFilter>,
  ): Observable<TableDataPayload<AdminBoxQuestionnaireCollectionItem>> {
    return this.api
      .fetchCollection(
        this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('name').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('ownerName').build(),
              )
              .appendText((builder) =>
                builder.caseInsensitiveLikeFor('orderNumber').build(),
              )
              .appendText((builder) => builder.inFor('status').build())
              .appendText((builder) => builder.inFor('selectTypes').build())
              .appendBoolean((builder) => builder.equalFor('hasError').build())
              .appendBoolean((builder) => builder.equalFor('isShown').build())
              .buildAsArray(),
          )
          .build(),
      )
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  new AdminBoxQuestionnaireCollectionItem({
                    contextOperations: this.mapContextOperations(
                      item.contextOperations,
                    ),
                    createdAt: item.createdAt,
                    endDate: item.endDate,
                    id: item.id,
                    isShown: item.isShown,
                    hasError: item.hasError,
                    errorMessage: item.errorMessage,
                    logoAlt: item.logo.alternativeText,
                    logoUrl: item.logo.url,
                    name: item.name,
                    orderId: item.order.id,
                    orderNumber: item.order.number,
                    ownerName: item.ownerName,
                    status: item.status,
                    selectTypes: item.selectTypes,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        ),
      );
  }

  public fetchDetails(id: Guid): Observable<AdminBoxQuestionnaireDetails> {
    return this.api.fetchDetails(id).pipe(
      map(
        (response) =>
          new AdminBoxQuestionnaireDetails({
            contextOperations: this.mapContextOperations(
              response.contextOperations,
            ),
            createdAt: response.createdAt,
            endDate: response.endDate,
            description: response.description,
            id: response.id,
            isShown: response.isShown,
            logoAlt: response.logo.alternativeText,
            logoUrl: response.logo.url,
            name: response.name,
            orderId: response.order.id,
            orderNumber: response.order.number,
            ownerName: response.ownerName,
            questions: response.questions.map(
              (question) =>
                new BoxQuestionnaireQuestion({
                  description: question.description,
                  id: question.id,
                  options: question.options.map(
                    (option) =>
                      new BoxQuestionnaireQuestionOption({
                        id: option.id,
                        imageAlt: option.featureFile?.alternativeText,
                        imageUrl: option.featureFile?.url,
                        isAvailable: option.isAvailable,
                        isAutoSelected: option.isAutoSelected,
                        isSelected: option.isSelected,
                        availableQuantity: option.availableQuantity,
                        remainingQuantity: option.remainingQuantity,
                        name: option.name,
                        sourceId: option.source?.id,
                        sourceName: option.source?.name,
                        attributes: valueOrDefault(option.attributes, []).map(
                          (attribute) =>
                            new BoxQuestionnaireQuestionOptionAttribute({
                              label: attribute.label,
                              id: attribute.id,
                              value: attribute.value,
                            }),
                        ),
                      }),
                  ),
                }),
            ),
            status: response.status,
          }),
      ),
    );
  }

  public confirmAndResumeById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wznowić wybraną ankietę? Ankieta zostanie przedłużona o 24h.')
          .accept(() => {
            this.api.resumeById(id).subscribe({
              next: () => {
                this.toast.show((builder) =>
                  builder
                    .initSuccess('Wybrana ankieta została wznowiona.')
                    .build(),
                );
                subscriber.next();
                subscriber.complete();
              },
              error: (error) => subscriber.error(error),
            });
          })
          .build(),
      );
    });
  }

  public confirmAndEndById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz zakończyć wybraną ankietę?')
          .accept(() => {
            this.api.endById(id).subscribe({
              next: () => {
                this.toast.show((builder) =>
                  builder
                    .initSuccess('Wybrana ankieta została zakończona.')
                    .build(),
                );
                subscriber.next();
                subscriber.complete();
              },
              error: (error) => subscriber.error(error),
            });
          })
          .build(),
      );
    });
  }

  public confirmAndChangeSelection(questionnaireId: Guid, questionId: Guid, optionId: Guid): Observable<void> {
    const request = new AdminBoxQuestionnaireChangeSelectionRequest({
      id: questionId,
      selectedOptionId: optionId,
    });

    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz zmienić wybór dla wybranego pytania? Poprzedni wybór zostanie cofnięty i wybrany nowy.')
          .accept(() => {
            this.api.changeSelection(questionnaireId, request).subscribe({
              next: () => {
                this.toast.show((builder) =>
                  builder
                    .initSuccess('Wybór został zmieniony.')
                    .build(),
                );
                subscriber.next();
                subscriber.complete();
              },
              error: (error) => subscriber.error(error),
            });
          })
          .build(),
      );
    });
  }

  private mapContextOperations(data: {
    canBeResumed: boolean;
    canBeEnded: boolean;
    canBeViewed: boolean;
    canBeSelectionChanged: boolean;
  }): AdminBoxQuestionnaireContextOperations {
    return new AdminBoxQuestionnaireContextOperations({
      canBeResumed: data.canBeResumed,
      canBeEnded: data.canBeEnded,
      canBeViewed: data.canBeViewed,
      canBeSelectionChanged: data.canBeSelectionChanged,
    });
  }
}
